import { ChangeEvent, useEffect, useState } from 'react';

import clsx from 'clsx';
import { Helmet } from 'react-helmet';

import { useTitleForPartner } from '@@/hooks/useTitleForPartner';
import { assetPath } from '@@/utils';

import analytics from '../../analytics';
import LayoutSelfService from '../LayoutSelfService/LayoutSelfService';
import Toggle from '../Toggle';

import imgPeeps from './peeps@3x.png';
import imgSofa from './sofa@3x.png';
import './AboutUsView.css';

type TTeam = {
  id: string;
  name: string;
  bioNow: string;
  bioThen: string;
  img?: string;
};

const team: TTeam[] = [
  {
    id: 'stephanie',
    name: 'Stephanie Lloyd',
    bioNow:
      'Toggle’s Head Honcho with deep experience in product, pricing, strategy, underwriting, sales and business development.',
    bioThen:
      'When she’s not Toggling, you can find her on the soccer pitch, doing nail art, crafting lattes or fiercely defending her corn hole champion title.',
    img: assetPath('img/about/Stephanie.png'),
  },
  {
    id: 'rehan',
    name: 'Rehan Ashroff',
    bioNow:
      'Rehan runs our innovation lab and is in charge of ideating and incubating new thinking for tech strategy and transformation.',
    bioThen: 'If there’s an app for that, chances are Rehan already has it.',
    img: assetPath('img/about/Rehan.png'),
  },
  {
    id: 'michelle',
    name: 'Michelle Dornfeld',
    bioNow:
      'Michelle leads all things Business Development at Toggle. She holds a degree in genetics but after 15 years, insurance is now in her DNA.',
    bioThen:
      'Former Army brat and proud mom to two active boys and two crazy pups. Passion for Hitchcock movies, mixed martial arts, world travel and anything crafty.',
    img: assetPath('img/about/Michelle.png'),
  },
  {
    id: 'josh',
    name: 'Josh LaRoche',
    bioNow:
      'Josh’s work passion is listening to our customers and making sure we’re exceeding their expectations.',
    bioThen:
      'Proud dad of three great kids. Likes keeping it real with a mix of fly-fishing, origami, gyotaku and brewing beer.',
    img: assetPath('img/about/Josh2.png'),
  },
  {
    id: 'ekta',
    name: 'Ekta Ranjan',
    bioNow:
      'When Toggle needs strategic planning and business transformation, we turn to Ekta and her expertise in lean-agile methodologies.',
    bioThen:
      'Coffee drinker. Netflix addict. Avid traveler. Likes to appear smarter by wearing fake prescription glasses. (Trust us, she’s smart.)',
    img: assetPath('img/about/Ekta.png'),
  },
  {
    id: 'hank',
    name: 'Hank Meisse',
    bioNow:
      'Hank is an actuary with more than 10 years of insurance experience in predictive modeling and product management. Fortunately, he studied math.',
    bioThen:
      'A perfect day involves grappling, whiskey, and bargain hunting the stock market.  Occasionally, he can be found hiking and exploring the outdoors.',
    img: assetPath('img/about/Hank.png'),
  },
  {
    id: 'todd',
    name: 'Todd Wright',
    bioNow:
      'Todd is the Toggler who connects people, technology, and business opportunities. His motto is always plan to re-plan.',
    bioThen:
      'Tech and learning are his muses. Can often be found reading, biking or making things. Not at the same time.',
    img: assetPath('img/about/Todd.png'),
  },
  {
    id: 'kimberly',
    name: 'Kimberly Dry',
    bioNow:
      'Kimberly loves creating new products that turn what’s expected into something new, different and above all, fun.',
    bioThen:
      'Follows the call of the wild through backpacking and bike touring and avoiding social media whenever possible.',
    img: assetPath('img/about/Kimberly.png'),
  },
  {
    id: 'christina',
    name: 'Christina Weber',
    bioNow:
      "Toggle's digital product lead who is our expert in understanding user psychology and turning them into 'lovable' experiences. Design thinking, user experience design, product marketing & -strategy are all up her alley.",
    bioThen:
      'Avid yogi with a love for life and cake, who exchanged the Austrian slopes for California beaches. Secretly wishing for more wall space as her passion for abstract painting is turning her apartment into a mini gallery.',
    img: assetPath('img/about/Christina.png'),
  },
];

function AboutUsView() {
  const { getTitle } = useTitleForPartner();
  const [teamToggles, setTeamToggles] = useState<{ [id: string]: boolean }>(
    team.reduce((acc, person) => {
      return {
        ...acc,
        [person.id]: false,
      };
    }, {})
  );

  useEffect(function onMount() {
    analytics.page();
  }, []);

  function handleToggleChange(e: ChangeEvent<HTMLInputElement>) {
    setTeamToggles({
      ...teamToggles,
      [e.target.name]: e.target.checked,
    });
  }

  return (
    <LayoutSelfService
      className="AboutUsView"
      header="At Toggle, we hit the refresh button on renters insurance."
      backgroundImgSrc={null}
      isLayoutContainer
      overrideClasses={{
        header_greeting: 'AboutUsView-header-greeting',
      }}
    >
      <Helmet>
        <title>{getTitle('About Us | Toggle Insurance')}</title>
        <meta
          name="description"
          content="Covered. Anytime. Anywhere. Learn about what makes our renters insurance subscriptions unique."
        />
      </Helmet>
      <div className="AboutUsView-section AboutUsView-section--mission">
        <div className="AboutUsView-container">
          <div className="AboutUsView-content">
            <h1 className="AboutUsView-heading">Our mission</h1>
            <p className="AboutUsView-tagline">
              To make renters insurance make sense for everyone.
            </p>
            <p className="AboutUsView-info">
              We give you complete control every step of the way, from covering
              your stuff anytime and anywhere to having your back as you reach
              for the next rung on the financial ladder.
            </p>
            <p className="AboutUsView-info">
              We make insurance easier to understand and more flexible to keep
              up with your ever-changing life.
            </p>
          </div>
        </div>
        <img
          src={imgPeeps}
          alt="Person taking laptop out of bookbag"
          className="AboutUsView-img"
        />
      </div>

      <div className="AboutUsView-section AboutUsView-section--story">
        <div className="AboutUsView-container">
          <div className="AboutUsView-content">
            <h2 className="AboutUsView-heading">Our story</h2>
            <p className="AboutUsView-tagline">
              We don’t act like your typical insurance company, because we’re
              not.
            </p>
            <p className="AboutUsView-info">
              But insurance is deep in our DNA-because we&apos;re a member of
              the Farmers
              <sup>&reg;</sup> Insurance family. Which means when you need us,
              we&apos;re there. And with over 90 years of experience, we&apos;re
              not fly by night.
            </p>
          </div>
        </div>
        <img
          src={imgSofa}
          alt="Sofa with lamp on a side table"
          className="AboutUsView-img"
        />
      </div>

      <div className="AboutUsView-section AboutUsView-section--team">
        <div className="AboutUsView-container">
          <div className="AboutUsView-content">
            <h2 className="AboutUsView-heading">Our team</h2>
            <p className="AboutUsView-tagline">We were born to Toggle.</p>
            <p className="AboutUsView-info">
              We think you&apos;ll like being part of the Toggle family. We take
              insurance seriously, but we don&apos;t take ourselves too
              seriously. When we aren&apos;t figuring out smarter ways to insure
              renters like you, we like to do stuff like knit, practice mixed
              martial arts, fly fish, craft lattes, play video games and more.
            </p>
            <p className="AboutUsView-info">
              Want to learn more about what makes us tick or what we looked like
              before we were insurance ninjas? Just toggle our photos and get a
              glimpse behind the curtain.
            </p>
          </div>
        </div>
        <ul className="AboutUsView-teamList">
          {team.map((person) => {
            const isChecked = teamToggles[person.id];
            return (
              <li
                className={clsx('AboutUsView-teamCard', {
                  'is-toggled': isChecked,
                })}
                key={person.id}
                style={{
                  backgroundImage: `url(${person.img})`,
                }}
              >
                <h4 className="AboutUsView-teamCard-heading">{person.name}</h4>
                <p className="AboutUsView-teamCard-bio">
                  {isChecked ? person.bioThen : person.bioNow}
                </p>
                <Toggle
                  name={person.id}
                  className="AboutUsView-teamCard-toggle"
                  checked={isChecked}
                  checkedLabel="Then"
                  uncheckedLabel="Now"
                  size="md"
                  onChange={handleToggleChange}
                />
              </li>
            );
          })}
        </ul>
      </div>

      <div className="AboutUsView-section AboutUsView-section--collab">
        <div className="AboutUsView-container">
          <h3 className="AboutUsView-heading">
            Interested in teaming up? Cool. Let&apos;s talk.
          </h3>
          <p className="AboutUsView-info">
            Let&apos;s put our thinking to work, together. If you like what you
            see and think you&apos;ve got the stuff it takes to make a business
            marriage work, let&apos;s talk.
          </p>
          <a
            href="mailto:letscollaborate@gettoggle.com"
            className="AboutUsView-collabCta btn btn-secondary btn-success btn-block"
          >
            Let&apos;s team up
          </a>
        </div>
      </div>
    </LayoutSelfService>
  );
}

export default AboutUsView;
